import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { UserKeys, getUserList } from '../api/users-api';
import { useState } from 'react';
import useDebounce from '../utils/useDebounce';
import UserAvatar from './user-avatar';
import { useUserSettings } from '../utils/use-user-settings';
import UsersBigQueryAutocomplete from './users-bigquery-autocomplete';
import { isFeatureEnabled } from '../utils/feature-flags';
import { emailRegex } from '../utils/regular-expressions';

import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';

interface UsersAutocompleteProps {
  autocompleteValue: string;
  setAutoCompleteValue: (value: string) => void;
  setAutoCompleteTypeValue?: (value: string) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  freeSolo?: boolean;
  newKey?: string;
  errors?: boolean;
  helperText?: string;
  variant?: 'standard' | 'outlined' | 'filled';
  onSelection?: (value: string) => void;
  label?: string;
}

export default function UsersAutocomplete({
  size,
  disabled,
  freeSolo,
  autocompleteValue,
  setAutoCompleteValue,
  setAutoCompleteTypeValue,
  errors,
  helperText,
  variant,
  onSelection,
  label,
}: UsersAutocompleteProps) {
  const { currentTier, features } = useUserSettings();
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(
    freeSolo ? autocompleteValue : inputValue,
    500
  );

  const usersEnabled = debouncedInput !== '' && autocompleteValue === '';

  const freeSoloUserEnabled = debouncedInput !== '';

  const enterpriseBackendIndexingEnabled =
    isFeatureEnabled('indexUsersReact', features) &&
    currentTier === 'enterprise';

  const { data: userData, isFetching: userLoading } = useQuery({
    queryKey: [UserKeys.UserList, { debouncedInput }],
    queryFn: () =>
      getUserList(
        {
          cursor: null,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 5,
        },
        debouncedInput,
        '',
        null,
        null
      ),
    enabled: freeSolo ? freeSoloUserEnabled : usersEnabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
  });

  interface Option {
    email: string;
    type: string;
    firstName?: string;
    lastName?: string;
    numberOfMembers?: number;
  }

  const userOptions: Option[] =
    userData?.results.map((result) => {
      return {
        email: result.userEmail,
        type: 'user',
        firstName: result.firstName,
        lastName: result.lastName,
      };
    }) || [];

  const freeSoloOption: Option[] =
    freeSolo &&
    autocompleteValue &&
    emailRegex.test(autocompleteValue) &&
    !userOptions.some((option) => option.email === autocompleteValue)
      ? [
          {
            email: autocompleteValue,
            type: 'user',
            firstName: '',
            lastName: '',
          },
        ]
      : [];

  const options = [...userOptions, ...freeSoloOption];

  function createNoOptionsText() {
    let text = 'No results found.';
    if (debouncedInput === '') {
      text = 'Start typing to search...';
    } else if (options.length === 0) {
      text = 'No results found.';
    }

    return text;
  }

  return enterpriseBackendIndexingEnabled ? (
    <UsersBigQueryAutocomplete
      autocompleteValue={autocompleteValue}
      setAutoCompleteValue={setAutoCompleteValue}
      setAutoCompleteTypeValue={setAutoCompleteTypeValue}
      errors={errors}
      helperText={helperText}
      size={size}
      disabled={disabled}
      freeSolo={freeSolo}
      variant={variant}
      onSelection={onSelection}
      label={label}
    />
  ) : (
    <Autocomplete
      id="users-autocomplete"
      disabled={disabled}
      filterOptions={(x) => x}
      options={options}
      slotProps={{
        paper: {
          sx: {
            minWidth: 400,
          },
        },
      }}
      size={size}
      autoComplete
      autoHighlight
      freeSolo={freeSolo}
      includeInputInList
      filterSelectedOptions={!freeSolo}
      noOptionsText={createNoOptionsText()}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return (
            userData?.results.find((user) => user.userEmail === option)
              ?.userEmail || option
          );
        } else {
          return option.email;
        }
      }}
      isOptionEqualToValue={(option, value) => {
        const valString =
          typeof value === 'string' ? value : value?.email || '';
        if (option.type === 'calendar') {
          return option.firstName === valString;
        }
        return option.email === valString;
      }}
      blurOnSelect={true}
      value={autocompleteValue}
      onChange={(_event, newValue, reason) => {
        let selectedValue = '';
        if (reason === 'clear') {
          setAutoCompleteValue('');
          if (onSelection) {
            onSelection('');
          }
          return;
        }

        if (!freeSolo) {
          if (typeof newValue === 'string') {
            setAutoCompleteValue(newValue);
            selectedValue = newValue;
          } else {
            selectedValue = newValue?.email || '';
            setAutoCompleteValue(selectedValue);

            if (setAutoCompleteTypeValue) {
              setAutoCompleteTypeValue(newValue?.type || '');
            }
          }
        } else {
          // freeSolo mode: handle both string and object values
          if (typeof newValue === 'string') {
            setAutoCompleteValue(newValue);
            selectedValue = newValue;
          } else if (newValue !== null) {
            selectedValue = newValue.email || newValue.firstName || '';
            setAutoCompleteValue(selectedValue);

            if (setAutoCompleteTypeValue) {
              setAutoCompleteTypeValue(newValue.type);
            }
          }
        }

        if (
          selectedValue &&
          (reason === 'selectOption' || reason === 'createOption') &&
          onSelection
        ) {
          onSelection(selectedValue);
        }
      }}
      renderOption={(props, option: Option) => {
        const { key, ...rest } = props;
        return (
          <ListItem key={key} {...rest} dense>
            <ListItemAvatar>
              <UserAvatar
                alt={`${option.firstName} ${option.lastName}`}
                userEmail={option.email}
              />
            </ListItemAvatar>
            <ListItemText
              id={option.email}
              primary={option.email}
              secondary={`${option.firstName} ${option.lastName}`}
            />
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || 'Search for a user'}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {userLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={errors}
          helperText={helperText}
          variant={variant || 'outlined'}
        />
      )}
      inputValue={freeSolo ? autocompleteValue : inputValue}
      onInputChange={(_event, newInputValue) => {
        if (freeSolo) {
          setAutoCompleteValue(newInputValue);
        } else {
          setInputValue(newInputValue);
        }
      }}
    />
  );
}
